import React, { useState } from "react";
import "./Sponsorship.css";

const SPONSORSHIP_TIERS = [
  {
    id: 1,
    name: "Gold Sponsor",
    amount: "$5,000+",
    color: "#FFD700",
    icon: "🥇",
    perks: ["Logo on all event banners", "Named in all community communications", "VIP seats at all events", "Certificate of recognition"],
  },
  {
    id: 2,
    name: "Silver Sponsor",
    amount: "$2,000–$4,999",
    color: "#C0C0C0",
    icon: "🥈",
    perks: ["Logo on website", "Mentioned at major events", "2 VIP event tickets", "Certificate of recognition"],
  },
  {
    id: 3,
    name: "Bronze Sponsor",
    amount: "$500–$1,999",
    color: "#CD7F32",
    icon: "🥉",
    perks: ["Name listed on website", "Mentioned at one event", "Certificate of recognition"],
  },
  {
    id: 4,
    name: "Community Supporter",
    amount: "Any amount",
    color: "#00703C",
    icon: "🤝",
    perks: ["Listed as a community supporter", "Our sincere gratitude"],
  },
];

export default function Sponsorship() {
  const [formData, setFormData] = useState({
    name        : "",
    organisation: "",
    email       : "",
    phone       : "",
    tier        : "",
    message     : "",
  });
  const [errors,   setErrors]   = useState({});
  const [loading,  setLoading]  = useState(false);
  const [sent,     setSent]     = useState(false);

  const validate = () => {
    const e = {};
    if (!formData.name.trim())  e.name  = "Your name is required";
    if (!formData.email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      e.email = "Please enter a valid email";
    if (!formData.tier) e.tier = "Please select a sponsorship tier";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);

    // Build mailto link as fallback, but also try backend email
    try {
      // Send via backend if available
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}members/sponsorship-enquiry/`,
        {
          method : "POST",
          headers: { "Content-Type": "application/json" },
          body   : JSON.stringify(formData),
        }
      );
      if (res.ok) {
        setSent(true);
      } else {
        throw new Error("Backend error");
      }
    } catch {
      // Fallback — open mailto
      const subject = encodeURIComponent(
        `Sponsorship Enquiry — ${formData.tier} — ${formData.name}`
      );
      const body = encodeURIComponent(
        `Name: ${formData.name}\n` +
        `Organisation: ${formData.organisation || "N/A"}\n` +
        `Email: ${formData.email}\n` +
        `Phone: ${formData.phone || "N/A"}\n` +
        `Tier: ${formData.tier}\n\n` +
        `Message:\n${formData.message}`
      );
      window.location.href =
        `mailto:info@marakwetcommunityinaustralia.org?subject=${subject}&body=${body}`;
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sponsorship-container">

      {/* Hero */}
      <div className="sponsor-hero">
        <h1 className="sponsorship-title">Partner With Us</h1>
        <p className="sponsorship-subtitle">
          Support the Marakwet Community in Australia and make a lasting impact.
          Your sponsorship helps fund events, welfare programmes, scholarships
          and community development initiatives.
        </p>
      </div>

      {/* Why sponsor */}
      <div className="why-sponsor">
        <h2>Why Sponsor MCIA?</h2>
        <div className="why-grid">
          <div className="why-card">
            <span>🌍</span>
            <h4>Community Impact</h4>
            <p>Directly support hundreds of Marakwet families living in Australia.</p>
          </div>
          <div className="why-card">
            <span>📣</span>
            <h4>Brand Visibility</h4>
            <p>Get your brand in front of a growing, engaged community audience.</p>
          </div>
          <div className="why-card">
            <span>🤝</span>
            <h4>CSR Goals</h4>
            <p>Fulfil your corporate social responsibility commitments meaningfully.</p>
          </div>
          <div className="why-card">
            <span>🏆</span>
            <h4>Recognition</h4>
            <p>Be recognised at events, on our website, and in community communications.</p>
          </div>
        </div>
      </div>

      {/* Sponsorship tiers */}
      <div className="tiers-section">
        <h2>Sponsorship Tiers</h2>
        <p className="tiers-sub">Choose a tier that suits your organisation.</p>
        <div className="tiers-grid">
          {SPONSORSHIP_TIERS.map(tier => (
            <div key={tier.id} className="tier-card"
              style={{ borderTop: `4px solid ${tier.color}` }}>
              <div className="tier-icon">{tier.icon}</div>
              <h3 className="tier-name" style={{ color: tier.color === "#FFD700" ? "#b8860b" : tier.color }}>
                {tier.name}
              </h3>
              <p className="tier-amount">{tier.amount}</p>
              <ul className="tier-perks">
                {tier.perks.map((perk, i) => (
                  <li key={i}>✓ {perk}</li>
                ))}
              </ul>
              <button
                className="tier-select-btn"
                style={{ background: tier.color === "#FFD700" ? "#b8860b" : tier.color }}
                onClick={() => {
                  setFormData(prev => ({ ...prev, tier: tier.name }));
                  document.getElementById("sponsor-form").scrollIntoView({ behavior: "smooth" });
                }}
              >
                Choose This Tier
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Contact form */}
      <div className="sponsor-form-section" id="sponsor-form">
        <h2>Get In Touch</h2>
        <p className="form-intro">
          Fill in the form below and our team will get back to you within 2 business days.
        </p>

        {sent ? (
          <div className="sponsor-success">
            <div className="success-icon">✅</div>
            <h3>Thank you for your interest!</h3>
            <p>
              We have received your sponsorship enquiry and will be in touch
              within 2 business days at <strong>{formData.email}</strong>.
            </p>
            <button className="send-another-btn"
              onClick={() => { setSent(false); setFormData({ name: "", organisation: "", email: "", phone: "", tier: "", message: "" }); }}>
              Submit Another Enquiry
            </button>
          </div>
        ) : (
          <form className="sponsor-form" onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-field">
                <label>Your Name *</label>
                <input name="name" value={formData.name}
                  onChange={handleChange} placeholder="Full name"
                  className={errors.name ? "error" : ""} />
                {errors.name && <span className="field-error">{errors.name}</span>}
              </div>
              <div className="form-field">
                <label>Organisation / Company</label>
                <input name="organisation" value={formData.organisation}
                  onChange={handleChange} placeholder="Your company name (optional)" />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label>Email Address *</label>
                <input name="email" type="email" value={formData.email}
                  onChange={handleChange} placeholder="your@email.com"
                  className={errors.email ? "error" : ""} />
                {errors.email && <span className="field-error">{errors.email}</span>}
              </div>
              <div className="form-field">
                <label>Phone Number</label>
                <input name="phone" value={formData.phone}
                  onChange={handleChange} placeholder="+61 400 000 000" />
              </div>
            </div>

            <div className="form-field">
              <label>Sponsorship Tier *</label>
              <select name="tier" value={formData.tier}
                onChange={handleChange}
                className={errors.tier ? "error" : ""}>
                <option value="">Select a tier...</option>
                {SPONSORSHIP_TIERS.map(t => (
                  <option key={t.id} value={t.name}>
                    {t.icon} {t.name} — {t.amount}
                  </option>
                ))}
                <option value="Custom">Custom / Other</option>
              </select>
              {errors.tier && <span className="field-error">{errors.tier}</span>}
            </div>

            <div className="form-field">
              <label>Message</label>
              <textarea name="message" value={formData.message}
                onChange={handleChange} rows={4}
                placeholder="Tell us about your organisation, what you'd like to sponsor, or any questions you have..." />
            </div>

            <button type="submit" className="submit-sponsor-btn" disabled={loading}>
              {loading ? "Sending…" : "Send Sponsorship Enquiry"}
            </button>

            <p className="form-note">
              Or email us directly at{" "}
              <a href="mailto:info@marakwetcommunityinaustralia.org">
                info@marakwetcommunityinaustralia.org
              </a>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}